import { RiFileLine } from '@remixicon/react';

import { Link } from '@backstage/core-components';

import { Text } from '@spotify-internal/encore-web';
import {
  extractLastNameComponent,
  useDocumentNavigation,
} from '@spotify/confidence-core-react';
import { useCollaborativeDocumentRef } from '@spotify/confidence-editor-react';
import { useExternalDocumentRef } from '@spotify/confidence-external-documents-react';
import {
  type ToolGroupEntry,
  ToolLabel,
  type ToolRenderOptions,
  createToolExtension,
} from '@spotify/plugin-confidence-chat-react';
import { UNTITLED_DOCUMENT_TITLE } from '@spotify/plugin-confidence-collaboration-react';

import { confidenceChatPlugin } from '../plugin';
import { countLines, formatLineCount } from './formatLineCount';
import { getInputString, getToolInput } from './parseToolResult';

import styles from './DocumentToolExtension.module.css';

type DocumentType = 'collaborative' | 'external' | 'unknown';

const getDocumentType = (name: string | undefined): DocumentType => {
  if (name?.startsWith('collaborativeDocuments/')) return 'collaborative';
  if (name?.startsWith('externalDocuments/')) return 'external';
  return 'unknown';
};

const DOCUMENT_TOOL_SUFFIXES = [
  'read_document',
  'write_document',
  'edit_document',
];

const getReadContent = (result: string): string => {
  if (!result.startsWith('<document ')) return result;
  const bodyStart = result.indexOf('\n\n');
  return bodyStart === -1 ? '' : result.slice(bodyStart + 2);
};

const getAction = (
  tool: string,
  {
    isStreaming,
    result,
  }: { isStreaming: boolean; result: ToolGroupEntry['result'] },
): string => {
  if (tool.endsWith('read_document')) {
    if (isStreaming || !result) return 'Reading';
    return result.success ? 'Read' : 'Failed to read';
  }
  if (tool.endsWith('edit_document')) {
    if (isStreaming || !result) return 'Editing';
    return result.success ? 'Edited' : 'Failed to edit';
  }
  if (isStreaming || !result) return 'Writing';
  return result.success ? 'Wrote' : 'Failed to write';
};

const getSummary = (
  entry: ToolGroupEntry,
  content: string | undefined,
  oldString: string | undefined,
  newString: string | undefined,
): string | undefined => {
  if (!entry.result) return undefined;
  if (!entry.result.success) return 'Failed';
  if (entry.tool.endsWith('edit_document')) {
    return `-${countLines(oldString ?? '')} +${countLines(newString ?? '')}`;
  }
  return content === undefined ? undefined : formatLineCount(content);
};

export const DocumentToolLabel = ({
  entry,
  options,
}: {
  entry: ToolGroupEntry;
  options: ToolRenderOptions;
}) => {
  const input = getToolInput(entry.input);
  const documentName = getInputString(input, 'name');
  const documentType = getDocumentType(documentName);

  const { collaborativeDocument } = useCollaborativeDocumentRef({
    name: documentName ?? '',
    skip: documentType !== 'collaborative',
  });
  const { externalDocument } = useExternalDocumentRef({
    name: documentName ?? '',
    skip: documentType !== 'external',
  });
  const { buildDocumentHref } = useDocumentNavigation();

  const isRead = entry.tool.endsWith('read_document');
  const isWrite = entry.tool.endsWith('write_document');
  const isEdit = entry.tool.endsWith('edit_document');
  const inputContent = getInputString(input, 'content', {
    preserveWhitespace: true,
  });
  const oldString = getInputString(input, 'old_string', {
    preserveWhitespace: true,
  });
  const newString = getInputString(input, 'new_string', {
    preserveWhitespace: true,
  });
  let readContent: string | undefined;
  if (isRead && entry.result) {
    readContent = entry.result.success
      ? getReadContent(entry.result.content)
      : entry.result.content;
  }
  const content = isWrite ? inputContent : readContent;
  const action = getAction(entry.tool, {
    isStreaming: options.isStreaming,
    result: entry.result,
  });
  const title =
    collaborativeDocument?.title ||
    externalDocument?.title ||
    getInputString(input, 'title') ||
    (options.isStreaming && isWrite ? 'document' : UNTITLED_DOCUMENT_TITLE);

  const documentId =
    documentType === 'collaborative'
      ? extractLastNameComponent(documentName)
      : undefined;
  const href = documentId ? buildDocumentHref(documentId) : undefined;
  const summary = getSummary(entry, content, oldString, newString);
  const streamingWriteTail =
    options.isStreaming && isWrite && inputContent
      ? inputContent.slice(-7)
      : undefined;

  return (
    <ToolLabel.Root>
      <ToolLabel.Primary>
        {action} {title}
      </ToolLabel.Primary>
      {(streamingWriteTail !== undefined || summary) && (
        <ToolLabel.Secondary>
          {streamingWriteTail ?? summary}
        </ToolLabel.Secondary>
      )}
      <ToolLabel.Timer startedAt={entry.result ? undefined : entry.startedAt} />
      <ToolLabel.Details>
        {href && (
          <Link to={href} className={styles.documentLink} data-track-source-anchor="cbcf3b3657">
            Open document
          </Link>
        )}
        {isEdit ? (
          <div className={styles.editDetails}>
            <div className={styles.oldSection}>
              <Text as="div" variant="bodySmallBold">
                Old
              </Text>
              <Text as="pre" variant="bodySmallMono" className={styles.content}>
                {oldString ?? 'Waiting for old content'}
              </Text>
            </div>
            <div className={styles.newSection}>
              <Text as="div" variant="bodySmallBold">
                New
              </Text>
              <Text as="pre" variant="bodySmallMono" className={styles.content}>
                {newString ?? 'Waiting for new content'}
              </Text>
            </div>
          </div>
        ) : (
          <div className={styles.documentDetails}>
            <Text as="pre" variant="bodySmallMono" className={styles.content}>
              {content ??
                (entry.result?.success
                  ? 'Empty document'
                  : 'Waiting for document content')}
            </Text>
          </div>
        )}
      </ToolLabel.Details>
    </ToolLabel.Root>
  );
};

export const DocumentToolExtension = confidenceChatPlugin.provide(
  createToolExtension({
    id: 'document-operation',
    predicate: tool =>
      DOCUMENT_TOOL_SUFFIXES.some(suffix => tool.endsWith(suffix)),
    icon: RiFileLine,
    display: 'grouped',
    collapseConsecutiveCalls: false,
    renderLabel: (entry, options) => (
      <DocumentToolLabel entry={entry} options={options} />
    ),
    render: () => null,
  }),
);
